import React, { useState, useEffect } from 'react';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
// New icons for maximize/minimize
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const API_URL = '/api';

const BOMViewModal = ({ open, onClose, bom }) => {
  const [bomData, setBomData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  
  // ---- Department screen-config state ----
  const SCREEN_NAME = 'BOM';
  const [screenConfig, setScreenConfig] = useState(null);
  
  // Toggle maximize/minimize function
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  useEffect(() => {
    if (open && bom) {
      // Reset maximize state when modal opens
      setIsMaximized(false);
      
      // Fetch screen configuration
      const fetchScreenConfig = async () => {
        try {
          const res = await fetch(`${API_URL}/screen-config/${encodeURIComponent(SCREEN_NAME)}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });

          if (!res.ok) {
            throw new Error(`Server returned ${res.status}`);
          }

          const data = await res.json();
          const config = Array.isArray(data) ? (data[0] || null) : data;
          setScreenConfig(config);
          console.log('BOM Screen Config:', config);
        } catch (err) {
          console.error('fetchScreenConfig error:', err);
          setScreenConfig(null);
        }
      };
      
      fetchScreenConfig();
      
      // If we already have the BOM data, use it directly
      if (bom && bom.BOMID) {
        setBomData(bom);
        return;
      }
      
      // Otherwise, fetch the data using the BOM ID
      const fetchBOMData = async () => {
        try {
          setLoading(true);
          const response = await fetch(`${API_URL}/bom/${bom.BOMID || bom}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });
          
          if (!response.ok) {
            throw new Error('Failed to fetch BOM data');
          }
          
          const data = await response.json();
          setBomData(data);
          console.log('BOM Data:', data);
        } catch (error) {
          console.error('Error fetching BOM data:', error);
          alert('Error loading BOM data');
        } finally {
          setLoading(false);
        }
      };
      
      fetchBOMData();
    } else {
      // Reset data when modal is closed
      setBomData(null);
    }
  }, [open, bom]);
  
  // Helper truthy & show-checkers (Department feature) - same as StockInViewModal
  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };

  // Helper to determine if Department should show in header
  const shouldShowDepartmentOnHeader = (cfg) => {
    if (!cfg) return false;
    const showHeader = truthy(cfg.ShowOnHeader) || truthy(cfg.showOnHeader);
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    return showHeader && showScreen; // BOTH required
  };

  // Helper to determine if Department should show in items
  const shouldShowDepartmentOnItems = (cfg) => {
    if (!cfg) return false;
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    const showItem = truthy(cfg.ShowOnItem) || truthy(cfg.showOnItem);
    return showScreen && showItem; // BOTH required
  };
  
  if (!open) return null;
  
  return (
    <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
        open ? 'translate-x-0' : 'translate-x-full'
      } ${
        isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl'
      }`}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">View BOM</h2>
            <div className="flex items-center gap-2">
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
              </button>
              <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Scrollable Content Area */}
        <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#6A89A7]"></div>
            </div>
          ) : (
            <div className="space-y-6 pb-6">
              
              
              {/* BOM Header */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">BOM Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">BOM Type</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {bomData?.BOMType || 'N/A'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">BOM Code</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {bomData?.BOMID || 'N/A'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">BOM Name</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {bomData?.BOMName || 'N/A'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Status</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {bomData?.Status || 'N/A'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">BOM Qty</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {bomData?.BOMQty ? parseFloat(bomData.BOMQty).toFixed(2) : 'N/A'}
                    </div>
                  </div>
                  
                  {/* Department in Header (controlled by screen-config) - same as StockInViewModal */}
                  {shouldShowDepartmentOnHeader(screenConfig) && (
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Department</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                        {bomData?.DepartmentName
                          ? `${bomData.DepartmentCode ? bomData.DepartmentCode + ' - ' : ''}${bomData.DepartmentName}`
                          : (bomData?.DepartmentCode || bomData?.DepartmentID || '-')}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Created By</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {bomData?.CreatedBy || 'N/A'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Created Date</label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {bomData?.CreatedAt ? new Date(bomData.CreatedAt).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                  {bomData?.UpdatedBy && (
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Last Updated By</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                        {bomData.UpdatedBy}
                      </div>
                    </div>
                  )}
                  {bomData?.UpdatedAt && (
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Last Updated Date</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                        {new Date(bomData.UpdatedAt).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* BOM Materials */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">BOM Materials</h3>
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="min-w-full border border-[#E2E8F0]">
                    <thead>
                      <tr className="bg-[#BDDDFC] text-[#384959]">
                        <th className="p-2">#</th>
                        <th className="p-2">Material Type</th>
                        <th className="p-2">Product Code</th>
                        <th className="p-2">Product Name</th>
                        <th className="p-2">UOM</th>
                        <th className="p-2">Qty</th>
                        <th className="p-2">Flush Type</th>
                        {/* Department column - Only shown if ShowOnItem is true (same as StockInViewModal) */}
                        {shouldShowDepartmentOnItems(screenConfig) && (
                          <th className="p-2">Department</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {bomData?.Details && bomData.Details.length > 0 ? (
                        bomData.Details.map((item, idx) => (
                          <tr key={idx} className="border-t border-[#E2E8F0]">
                            <td className="p-2 text-center">{idx + 1}</td>
                            <td className="p-2">
                              <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                {item.MaterialType || 'N/A'}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                {item.ProductCode || 'N/A'}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="w-40 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                {item.ProductName || 'N/A'}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                {item.UOM || 'N/A'}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                {item.Qty ? parseFloat(item.Qty).toFixed(2) : 'N/A'}
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                {item.FlushType || 'N/A'}
                              </div>
                            </td>
                            
                            {/* Department cell - Only shown if ShowOnItem is true (same as StockInViewModal) */}
                            {shouldShowDepartmentOnItems(screenConfig) && (
                              <td className="p-2">
                                <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                  {item.DepartmentCode || '-'}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={shouldShowDepartmentOnItems(screenConfig) ? 8 : 7} className="p-4 text-center text-gray-500">
                            No materials found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Fixed Footer with Buttons */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button 
              type="button" 
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BOMViewModal;